import { useState } from 'react';
import type { ReactNode } from 'react';

import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { Chip, IconButton, InputAdornment, Stack, TextField } from '@mui/material';

import type { MaskedCredential } from '../../model/settingsTypes';
import { FieldRow } from './FieldRow';

export interface MaskedCredentialFieldProps {
  label: ReactNode;
  description?: ReactNode;
  /** The masked credential as returned by the BFF (never the raw secret). */
  credential: MaskedCredential | null;
  /** The write-only replacement secret (controlled). */
  secret: string;
  onSecretChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Renders a provider/engine secret safely: the current value is shown only as a
 * masked status chip, and a write-only input lets an operator set a replacement.
 * Leaving the input blank keeps the existing secret (per the contract).
 */
export function MaskedCredentialField({
  label,
  description,
  credential,
  secret,
  onSecretChange,
  disabled = false,
  placeholder = 'Enter a new secret to replace',
}: MaskedCredentialFieldProps) {
  const [reveal, setReveal] = useState(false);
  const present = credential?.present ?? false;

  return (
    <FieldRow label={label} description={description} alignTop>
      <Stack spacing={1}>
        <Chip
          size="small"
          variant="outlined"
          color={present ? 'success' : 'default'}
          label={present ? `Configured · ${credential?.masked ?? '••••'}` : 'Not configured'}
          sx={{ alignSelf: 'flex-start' }}
        />
        <TextField
          size="small"
          fullWidth
          type={reveal ? 'text' : 'password'}
          value={secret}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(event) => onSecretChange(event.target.value)}
          autoComplete="new-password"
          helperText="Leave blank to keep the current secret."
          slotProps={{
            htmlInput: { 'aria-label': typeof label === 'string' ? label : 'secret' },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label={reveal ? 'Hide secret' : 'Show secret'}
                    onClick={() => setReveal((value) => !value)}
                    disabled={disabled}
                  >
                    {reveal ? (
                      <VisibilityOffRoundedIcon fontSize="small" />
                    ) : (
                      <VisibilityRoundedIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Stack>
    </FieldRow>
  );
}
